import * as THREE from 'three';
import { PERSON_LANDMARKS as P } from './anatomy.js';

export const STRIDE_HALF = .29;
const TAU = Math.PI * 2, UPPER_LEG = -P.kneeY, LOWER_LEG = P.hipY+P.kneeY-P.ankleY;
const clamp = THREE.MathUtils.clamp, mix = THREE.MathUtils.lerp;
const v = new THREE.Vector3(), q = new THREE.Quaternion(), e = new THREE.Euler(), one = new THREE.Vector3(1,1,1);
const armRoll = new THREE.Quaternion(), armAxis = new THREE.Vector3(0,1,0);

export function createPersonPose() {
  return { drop:0, sit:0, legL:{},legR:{},armL:{},armR:{} };
}

function leg(out, phase, gait, swing, sit, drop) {
  const t=((phase%TAU)+TAU)%TAU/Math.PI;
  const u=t<1?t:t-1, lift=t<1?0:Math.sin(u*Math.PI);
  const z=t<1?STRIDE_HALF*(1-2*u):STRIDE_HALF*(-1+2*u*u*(3-2*u));
  out.z=mix(mix(-swing*.32,z,gait),.42,sit);
  out.y=P.ankleY-drop+mix(.08*Math.max(0,swing)*(1-gait)+.12*lift*gait,0,sit);
  out.pitch=-.16*lift*gait*(1-sit);
  const dy=P.hipY-out.y, d=clamp(Math.hypot(dy,out.z),.01,UPPER_LEG+LOWER_LEG);
  const knee=Math.PI-Math.acos(clamp((UPPER_LEG**2+LOWER_LEG**2-d*d)/(2*UPPER_LEG*LOWER_LEG),-1,1));
  out.hip=-Math.atan2(out.z,dy)-Math.acos(clamp((UPPER_LEG**2+d*d-LOWER_LEG**2)/(2*UPPER_LEG*d),-1,1));
  out.knee=knee;
}

export function evaluatePersonPose(anim, out) {
  const gait=clamp(anim.gait??0,0,1), sit=clamp(anim.sit??0,0,1), swing=anim.swing??0;
  out.sit=sit;
  // Small idle weight shifts lower the body while IK keeps the supporting foot
  // planted. Moving the whole avatar vertically would also move both soles.
  out.drop=mix(-.06*gait-.035*Math.abs(swing)*(1-gait)-.4*Math.max(0,1-(anim.squashY??1)),-.35,sit)-clamp(anim.settle??0,0,.04);
  leg(out.legL,anim.phase??0,gait,swing,sit,out.drop);
  leg(out.legR,(anim.phase??0)+Math.PI,gait,-swing,sit,out.drop);
  for (let side=-1;side<=1;side+=2) {
    const arm=side<0?out.armL:out.armR, wave=side<0?(anim.waveZL??0):(anim.waveZ??0);
    const greeting=THREE.MathUtils.smoothstep(wave,0,1.6)*(1-sit);
    // Relaxed elbows and palms turned toward the body. Blend continuously into
    // the raised greeting pose, keeping the hand orientation stable at wave=0.
    arm.x=mix(mix(-.04+side*swing*.65,-.14,greeting),-.35,sit);
    const raised=wave<=1.6?wave:1.6+.65*Math.tanh((wave-1.6)/.65);
    arm.z=side*mix(.07,raised,greeting)*(1-.6*sit);
    arm.roll=side*mix(.9*(1-.4*sit),Math.PI,greeting);
    arm.bend=mix(mix(-.18-.20*Math.min(1,Math.abs(swing)),.50,greeting),-.50,sit);
  }
  return out;
}

export function personPartMatrix(def, pose, target) {
  let bend=0;
  if (def.anim==='legL'||def.anim==='legR') {
    const leg=pose[def.anim]; e.set(leg.hip,0,0); v.fromArray(def.pos); bend=leg.knee;
  } else if (def.anim==='footL'||def.anim==='footR') {
    const leg=pose[def.anim==='footL'?'legL':'legR']; e.set(leg.pitch,0,0);
    v.set(def.pos[0],leg.y-Math.sin(leg.pitch)*def.pos[2],leg.z+Math.cos(leg.pitch)*def.pos[2]);
  } else if (def.anim==='armL'||def.anim==='armR') {
    const arm=pose[def.anim]; e.set(arm.x,0,arm.z); v.fromArray(def.pos); bend=arm.bend;
  } else {
    e.set(def.rot?.[0]??0,def.rot?.[1]??0,def.rot?.[2]??0); v.fromArray(def.pos); bend=def.deform==='cloth'?pose.sit:0;
  }
  q.setFromEuler(e);
  if (def.anim==='armL'||def.anim==='armR') {
    // Roll around the arm's own axis before the shoulder rotation. Skin and
    // sleeves share this transform, preserving their alignment and draw calls.
    q.multiply(armRoll.setFromAxisAngle(armAxis,pose[def.anim].roll));
  }
  target.compose(v,q,one);
  return bend;
}

const previewPose=createPersonPose(), matrix=new THREE.Matrix4();
// Shared by the model studio, achievement banner/chips and diagnostic captures.
export function applyPersonPose(group, anim) {
  const pose=evaluatePersonPose(anim,previewPose);
  for (const mesh of group.children) {
    const def=mesh.userData.part;
    if (!def) continue;
    const bend=personPartMatrix(def,pose,matrix);
    matrix.decompose(mesh.position,mesh.quaternion,mesh.scale);
    if (mesh.material.personDeform) mesh.material.personDeform.value=bend;
    mesh.frustumCulled=false;
  }
  return pose.drop;
}
